import * as React from 'react'
import { Check, ChevronDown, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps {
  options: SelectOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  label?: string
  error?: string
  hint?: string
  disabled?: boolean
  loading?: boolean
  className?: string
  id?: string
  allowCustom?: boolean
  commitWhenLength?: number
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  maxLength?: number
}

export function Select({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  label,
  error,
  hint,
  disabled,
  loading = false,
  className,
  id,
  allowCustom = false,
  commitWhenLength,
  inputMode,
  maxLength,
}: SelectProps) {
  const selectId = id ?? React.useId()
  const listboxId = `${selectId}-listbox`
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1)

  const selectedOption = options.find((o) => o.value === value)
  const displayLabel = selectedOption?.label ?? value ?? ''

  const filtered = search
    ? options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
        || o.value.toLowerCase().includes(search.toLowerCase()),
      )
    : options

  function commitCustomValue(raw?: string) {
    const next = (raw ?? search).trim()
    if (!next) return false
    if (!allowCustom && !options.some((o) => o.value === next)) return false
    onValueChange?.(next)
    setOpen(false)
    setSearch('')
    inputRef.current?.blur()
    return true
  }

  // Close on outside click
  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (allowCustom && search.trim()) {
          commitCustomValue()
          return
        }
        setOpen(false)
        setSearch('')
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  // Reset highlight when filtered list changes
  React.useEffect(() => {
    setHighlightedIndex(-1)
  }, [search])

  function handleSelect(optValue: string) {
    onValueChange?.(optValue)
    setOpen(false)
    setSearch('')
    inputRef.current?.blur()
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (disabled) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
        } else {
          setHighlightedIndex((prev) => {
            const next = prev + 1
            return next >= filtered.length ? 0 : next
          })
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open) {
          setHighlightedIndex((prev) => {
            const next = prev - 1
            return next < 0 ? filtered.length - 1 : next
          })
        }
        break
      case 'Enter':
        e.preventDefault()
        if (open && highlightedIndex >= 0 && filtered[highlightedIndex]) {
          const opt = filtered[highlightedIndex]
          if (!opt.disabled) handleSelect(opt.value)
        } else if (open && allowCustom && search.trim()) {
          commitCustomValue()
        } else if (!open) {
          setOpen(true)
        }
        break
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        setSearch('')
        break
      case 'Tab':
        setOpen(false)
        setSearch('')
        break
    }
  }

  // Scroll highlighted item into view
  const listRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label htmlFor={selectId} className="mb-1.5 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={selectId}
          type="text"
          role="combobox"
          inputMode={inputMode}
          maxLength={maxLength}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            highlightedIndex >= 0 ? `${selectId}-option-${highlightedIndex}` : undefined
          }
          aria-invalid={!!error}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center rounded-md border border-gray-300 bg-white px-3 py-2 pr-16 text-sm',
            'placeholder:text-gray-400',
            'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
            'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            className,
          )}
          placeholder={displayLabel || placeholder}
          value={open ? search : displayLabel}
          onChange={(e) => {
            const next = e.target.value
            setSearch(next)
            if (!open) setOpen(true)
            if (
              commitWhenLength
              && allowCustom
              && next.trim().length >= commitWhenLength
            ) {
              onValueChange?.(next.trim())
              setOpen(false)
              setSearch('')
              inputRef.current?.blur()
            }
          }}
          onFocus={() => {
            setOpen(true)
            setSearch('')
          }}
          onBlur={() => {
            if (allowCustom && search.trim() && search.trim() !== (value ?? '')) {
              commitCustomValue()
              return
            }
            setOpen(false)
            setSearch('')
          }}
          onKeyDown={handleKeyDown}
        />
        <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
          {loading && (
            <Loader2 className="h-4 w-4 animate-spin text-primary-600" aria-hidden />
          )}
          {value && !disabled && !loading && (
            <button
              type="button"
              tabIndex={-1}
              className="rounded p-0.5 text-gray-400 hover:text-gray-600"
              onClick={(e) => {
                e.stopPropagation()
                onValueChange?.('')
                setSearch('')
              }}
              aria-label="Clear selection"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-400 transition-transform',
              open && 'rotate-180',
            )}
          />
        </div>

        {/* Dropdown */}
        {open && (
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          >
            {filtered.length === 0 ? (
              allowCustom && search.trim() ? (
                <div
                  role="option"
                  className="cursor-pointer px-3 py-2 text-sm text-primary-700 hover:bg-primary-50"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    commitCustomValue()
                  }}
                >
                  Use &quot;{search.trim()}&quot;
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-400">No results found</div>
              )
            ) : (
              filtered.map((opt, idx) => {
                const isSelected = opt.value === value
                const isHighlighted = idx === highlightedIndex
                return (
                  <div
                    key={opt.value}
                    id={`${selectId}-option-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled}
                    className={cn(
                      'relative flex cursor-default select-none items-center px-3 py-2 text-sm',
                      isHighlighted && 'bg-primary-50 text-primary-700',
                      isSelected && !isHighlighted && 'bg-gray-50',
                      opt.disabled && 'pointer-events-none opacity-50',
                      !opt.disabled && !isHighlighted && 'hover:bg-gray-50',
                    )}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault() // prevent blur
                      if (!opt.disabled) handleSelect(opt.value)
                    }}
                  >
                    <span className="flex-1">{opt.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary-600" />}
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {!error && hint && (
        <p className="mt-1 text-sm text-gray-500">{hint}</p>
      )}
    </div>
  )
}
